/* External */
import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import debounceL from 'lodash-es/debounce';
import { useIntl } from 'react-intl';
import classnames from 'classnames';

/* Ott-common */
import { assetTypes } from '@dtvgo/rtk-query-api';
import {
  PlayerOverlay, AudioSubtitleButton, CastButton, ConditionalVisible, ControlBar, LiveButton,
  PlayPauseButton, RemainingTime, RewindButton, ViewModeButton, VolumeButton,
} from '@dtvgo/web-player-ui';
import {
  useIsDelayed, useLiveDuration, useLiveProgress, useLiveProgressBarInfo,
} from '@dtvgo/player-utils-react';
import { Tracks } from '@dtvgo/player-utils';
import { useGetSync } from '@dtvgo/react-utils';
import {
  PlayerState, PlayerViewMode, isPlayerLoaded, useBuffering, usePlayerActions,
  usePlayerDuration, usePlayerState, usePlayerTime, usePlayerViewMode, usePlayerVolume,
  useSeeking,
} from '@dtvgo/player-context';

/* Components */
import {
  KeyMomentsContainer,
  KeyMomentCutPreview,
  KeyMomentsMarkers,
  keyMomentRowTouchesUnavailable,
} from './keyMoments';
import KeyMomentsImmersiveHeader from './keyMoments/KeyMomentsImmersiveHeader';
import nearestKeyMomentOnBar from './keyMoments/nearestKeyMomentOnBar';
import useKeyMomentsPlayer from './keyMoments/useKeyMomentsPlayer';
import LoaderScreen from './loaderScreen/LoaderScreen';
import PlayerDock from './dock/PlayerDock';
import AreYouStillWatchingMessage from './areYouStillWatchingMessage/AreYouStillWatchingMessage';
import FifaPlayerSideButton from '../components/fifaPlayerSideButton/FifaPlayerSideButton';
import FifaPlayerNotification from '../components/fifaPlayerNotification/FifaPlayerNotification';
import MiniEPG from './miniEPG/MiniEPG';
import { LiveProgressBar, VodProgressBar } from './ProgressBar';

/* Other */
import {
  IS_BRAZIL, USE_PLAYER_UI_V2, getGtmEventConfig,
  USE_LL_EXTENDED_SEEK, LL_EXTENDED_SEEK_CHANNELS,
} from '../utils';
import { useAreYouStillWatchingTime } from './utils/hooks';
import { gtmDimensions } from '../components/GtmContext/dimensions/player';

/* Styles */
import './PlayerUI.scss';

/* Components PlayerV2 */
import EpgButtonV2 from './v2/core/EpgButton/EpgButton';
import RewindTimeButtonV2 from './v2/core/RewindTimeButton/RewindTimeButton';
import FowardTimeButtonV2 from './v2/core/FowardTimeButton/FowardTimeButton';
import KeyMomentsButton from './v2/core/KeyMomentsButton/KeyMomentsButton';
import NextEpiButtonV2 from './v2/core/NextEpiButton/NextEpiButton';
import MoreChannelsButtonV2 from './v2/core/MoreChannelsButton/MoreChannelsButton';
import EpisodesButtonV2 from './v2/core/EpisodesButton/EpisodesButton';
import PlayPauseButtonV2 from './v2/core/PlayPauseButton/PlayPauseButton';
import LiveButtonV2 from './v2/core/LiveButton/LiveButton';
import VolumeButtonV2 from './v2/core/VolumeButton/VolumeButton';
import AudioSubtitleButtonV2 from './v2/core/AudioSubtitleButton/AudioSubtitleButton';
import ViewModeButtonV2 from './v2/core/ViewModeButton/ViewModeButton';
import PlayerDockV2 from './v2/dock/PlayerDock';
import LoaderScreenV2 from './v2/loaderScreen/LoaderScreen';

const INACTIVITY_TIME_TO_HIDE_CONTROLS_MS = 4000;
const SECONDS_TO_REWIND = 30;
const SECONDS_TO_FRONTWIND = -30;
const SECONDS_TO_REWIND_V2 = 10;
const SECONDS_TO_FRONTWIND_V2 = -10;

const PlayerUI = forwardRef(({
  isLive,
  asset,
  channelId,
  channelLogo,
  channelName,
  nextAsset,
  readyToPlay,
  displayAreYouStillWatching,
  showPlayButton,
  gtmInteraction,
  onUserInteraction,
  onGoLive,
  onBeforePlay,
  authorizerVrioId,
  hasNextEpi,
  nextAssetIsLoading,
  seekToEpoch,
  isTimeShiftLoadingRef,
  onTimeShiftLoadComplete,
}, ref) => {
  const [isUserActive, setIsUserActive] = useState(true);
  const [subtitleTracks, setSubtitleTracks] = useState([]);
  const [audioTracks, setAudioTracks] = useState([]);
  const [
    isAreYouStillWatchingActive,
    setIsAreYouStillWatchingActive,
  ] = useState(false);
  const [isMiniEpgOpen, setIsMiniEpgOpen] = useState(false);

  const state = usePlayerState();
  const { isSeeking } = useSeeking();
  const isBuffering = useBuffering();
  const playerTime = usePlayerTime();
  const duration = usePlayerDuration();
  const liveDuration = useLiveDuration();
  const viewMode = usePlayerViewMode();
  const volume = usePlayerVolume();
  const { formatMessage } = useIntl();

  const orderByIso = IS_BRAZIL ? Tracks.TrackLanguageOrder.BR : Tracks.TrackLanguageOrder.SSLA;
  const sortAudioTracks = useMemo(() => Tracks.sortAudioTracks(orderByIso), [orderByIso]);
  const sortTextTracks = useMemo(() => Tracks.sortTextTracks(orderByIso), [orderByIso]);

  const playerActions = usePlayerActions();

  const updateSubtitles = useCallback(() => {
    const subs = playerActions.getAvailableSubtitles();
    if (subs.length === 0) {
      setSubtitleTracks([]);
    } else {
      setSubtitleTracks(
        sortTextTracks(
          Tracks.addIndexToUnknownTracks(Tracks.parseTextTracks(subs)),
          orderByIso,
        ),
      );
    }
  }, [orderByIso, playerActions, sortTextTracks]);

  const updateAudio = useCallback(() => {
    const audio = playerActions.getAvailableAudio();
    if (audio.length === 0) {
      setAudioTracks([]);
    } else {
      setAudioTracks(
        sortAudioTracks(
          Tracks.addIndexToUnknownTracks(Tracks.parseAudioTracks(audio)),
          orderByIso,
        ),
      );
    }
  }, [playerActions, sortAudioTracks, orderByIso, setAudioTracks]);

  const updateTracks = useCallback(() => {
    updateSubtitles();
    updateAudio();
  }, [updateAudio, updateSubtitles]);

  const isPaused = !isPlayerLoaded(state)
    || state === PlayerState.PAUSED
    || state === PlayerState.LOADED;

  const liveProgress = useLiveProgress();
  const isDelayed = useIsDelayed();
  const { seekable: liveSeekable, handleSeek: handleLiveSeek } = useLiveProgressBarInfo();

  const progress = isLive ? liveProgress : playerTime;

  /** KM carousel (V2). Placed before KM hook so open/close resets carousel index without seek. */
  const [isKeyMomentsCarouselOpen, setIsKeyMomentsCarouselOpen] = useState(false);

  const keyMoments = useKeyMomentsPlayer({
    asset,
    isLive,
    handleLiveSeek,
    seekable: liveSeekable,
    authorizerVrioId,
    playerUiV2: USE_PLAYER_UI_V2,
    keyMomentsCarouselOpen: USE_PLAYER_UI_V2 && isKeyMomentsCarouselOpen,
    useExtendedDvr: USE_LL_EXTENDED_SEEK && LL_EXTENDED_SEEK_CHANNELS.has(channelId),
    onSeekOutsideWindow: seekToEpoch,
  });

  const [cutPreviewPointer, setCutPreviewPointer] = useState(null);
  const progressBarHoverZoneRef = useRef(null);

  useEffect(() => {
    if (!keyMoments.enabled) setIsKeyMomentsCarouselOpen(false);
  }, [keyMoments.enabled]);

  const handleKeyMomentsToolbarToggle = useCallback(() => {
    setIsKeyMomentsCarouselOpen((open) => !open);
  }, []);

  const handleDismissKeyMomentsCarousel = useCallback(() => {
    setIsKeyMomentsCarouselOpen(false);
  }, []);

  const handleKeyMomentCarouselSelect = useCallback((item) => {
    if (!item || keyMomentRowTouchesUnavailable(item, keyMoments.unavailableIds)) return;
    keyMoments.onSelect(item);
    setIsKeyMomentsCarouselOpen(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- onSelect stable in useKeyMomentsPlayer
  }, [keyMoments.onSelect, keyMoments.unavailableIds]);

  const isPlaying = state === PlayerState.PLAYING;
  const getIsPlaying = useGetSync(isPlaying);
  const prevIsSeeking = useRef(false);
  // Use a ref (not React state) so isTimeShiftLoading is already true when
  // the ValueStore-driven render (from ShakaPlayer's state.set(LOADING)) fires.
  const isTimeShiftLoading = isTimeShiftLoadingRef?.current ?? false;

  const isLoading = !readyToPlay
    || !state
    || (!isTimeShiftLoading && !isPlayerLoaded(state))
    || nextAssetIsLoading;

  // When a time-shift reload completes (player reaches LOADED), clear the flag.
  useEffect(() => {
    if (isTimeShiftLoading && isPlayerLoaded(state)) {
      onTimeShiftLoadComplete();
    }
  }, [state, isTimeShiftLoading, onTimeShiftLoadComplete]);
  const shouldReportSeekedEvent = useRef(false);

  const handleGtmEvent = useCallback((type) => {
    const { hash, dimensions, eventType } = getGtmEventConfig(
      asset.type,
      gtmDimensions,
      'player',
      type,
    );
    gtmInteraction(asset, hash, eventType, dimensions);
  }, [asset, gtmInteraction]);

  const togglePlayPause = useCallback(() => {
    shouldReportSeekedEvent.current = true;
    const localIsPlaying = getIsPlaying();
    if (!localIsPlaying) onBeforePlay();
    playerActions.togglePlayPause();
    handleGtmEvent(localIsPlaying ? 'pause' : 'play');
  }, [getIsPlaying, handleGtmEvent, onBeforePlay, playerActions]);

  const rewind30 = useCallback(() => {
    const totalRewind = !USE_PLAYER_UI_V2 ? SECONDS_TO_REWIND : SECONDS_TO_REWIND_V2;
    shouldReportSeekedEvent.current = false;
    playerActions.rewind(totalRewind);
    handleGtmEvent('backward');
  }, [handleGtmEvent, playerActions]);

  const toggleMute = useCallback(() => {
    shouldReportSeekedEvent.current = true;
    playerActions.toggleMute();
  }, [playerActions]);

  const setVolume = useCallback((newVolume) => {
    shouldReportSeekedEvent.current = true;
    playerActions.setVolume(newVolume);
  }, [playerActions]);

  const selectAudioTrack = useCallback((track) => {
    shouldReportSeekedEvent.current = true;
    setAudioTracks((prev) => prev.map((t) => ({ ...t, isSelected: t.id === track.id })));
    playerActions.selectAudioTrack(track);
    handleGtmEvent('change_audio');
  }, [handleGtmEvent, playerActions]);

  const selectSubtitleTrack = useCallback((track) => {
    shouldReportSeekedEvent.current = true;
    setSubtitleTracks((prev) => prev.map((t) => ({ ...t, isSelected: t.id === track.id })));
    playerActions.selectSubtitleTrack(track);
    handleGtmEvent('change_subtitle');
  }, [handleGtmEvent, playerActions]);

  const disableSubtitles = useCallback(() => {
    shouldReportSeekedEvent.current = true;
    setSubtitleTracks((prev) => prev.map((t) => ({ ...t, isSelected: false })));
    playerActions.disableSubtitles();
    handleGtmEvent('change_subtitle');
  }, [handleGtmEvent, playerActions]);

  const goToLive = useCallback(() => {
    shouldReportSeekedEvent.current = false;
    // eslint-disable-next-line no-param-reassign
    if (isTimeShiftLoadingRef) isTimeShiftLoadingRef.current = true;
    playerActions.goToLive();
    onGoLive();
    handleGtmEvent('ao_vivo');
  }, [handleGtmEvent, onGoLive, playerActions, isTimeShiftLoadingRef]);

  const toggleFullScreen = useCallback(() => {
    shouldReportSeekedEvent.current = true;
    playerActions.toggleFullScreen();
    handleGtmEvent('full_screen');
  }, [handleGtmEvent, playerActions]);

  const scheduleSetUserInactive = useMemo(() => debounceL(
    () => setIsUserActive(false),
    INACTIVITY_TIME_TO_HIDE_CONTROLS_MS,
  ), []);

  const keyMomentsImmersiveOpen = keyMoments.enabled && isKeyMomentsCarouselOpen;

  const chromeVisible = isUserActive || keyMomentsImmersiveOpen;
  const keyMomentsFocusActive = keyMomentsImmersiveOpen;

  const cutPreviewView = useMemo(() => {
    if (!cutPreviewPointer || !keyMoments.enabled) return null;
    const item = keyMoments.items.find(
      (row) => row.incidenceId === cutPreviewPointer.incidenceId,
    );
    if (!item) return null;
    return {
      item,
      positionPct: cutPreviewPointer.positionPct,
    };
  }, [cutPreviewPointer, keyMoments.enabled, keyMoments.items]);

  useEffect(() => {
    if (!isUserActive) {
      setCutPreviewPointer(null);
    }
  }, [isUserActive]);

  useEffect(() => {
    if (keyMomentsImmersiveOpen) {
      setCutPreviewPointer(null);
    }
  }, [keyMomentsImmersiveOpen]);

  const handleKeyMomentMarkerSelect = useCallback((item) => {
    if (!item || keyMomentRowTouchesUnavailable(item, keyMoments.unavailableIds)) return;
    keyMoments.onSelect(item);
  }, [keyMoments]);

  const handleProgressBarHoverMouseMove = useCallback((event) => {
    if (!keyMoments.enabled || !chromeVisible) return;
    const zone = progressBarHoverZoneRef.current;
    if (!zone) return;

    const strip = zone.querySelector('.dtv-common-c-key-moments__markers');
    const holder = zone.querySelector('.dtv-common-c-slider__holder');
    const geom = strip?.getBoundingClientRect() ?? holder?.getBoundingClientRect();
    if (!geom || geom.width <= 0) return;

    const px = Math.min(geom.width, Math.max(0, event.clientX - geom.left));
    const near = nearestKeyMomentOnBar({
      items: keyMoments.items,
      streamStartTimestamp: keyMoments.streamStartTimestamp,
      durationSeconds: liveDuration,
      pxAlongStrip: px,
      stripWidthPx: geom.width,
    });
    if (!near) {
      setCutPreviewPointer(null);
      return;
    }

    setCutPreviewPointer({
      incidenceId: near.item.incidenceId,
      positionPct: near.positionPct,
    });
  }, [chromeVisible, keyMoments, liveDuration]);

  const handleProgressBarHoverMouseLeave = useCallback(() => {
    setCutPreviewPointer(null);
  }, []);

  const areYouStillWatchingTime = useAreYouStillWatchingTime(isLive);
  const getDisplayAreYouStillWatching = useGetSync(displayAreYouStillWatching);

  const scheduleDisplayAreYouStillWatching = useMemo(() => debounceL(
    () => {
      playerActions.pause();
      setIsAreYouStillWatchingActive(true);
    },
    areYouStillWatchingTime,
  ), [areYouStillWatchingTime, playerActions]);

  const setUserActive = useCallback(() => {
    setIsUserActive(true);
    if (getIsPlaying() && !keyMomentsImmersiveOpen) {
      scheduleSetUserInactive();
    } else {
      scheduleSetUserInactive.cancel();
    }
    if (getDisplayAreYouStillWatching()) {
      scheduleDisplayAreYouStillWatching();
    } else {
      scheduleDisplayAreYouStillWatching.cancel();
    }
    onUserInteraction();
  }, [
    getIsPlaying,
    keyMomentsImmersiveOpen,
    getDisplayAreYouStillWatching,
    onUserInteraction,
    scheduleSetUserInactive,
    scheduleDisplayAreYouStillWatching,
  ]);

  const continueWatching = useCallback(() => {
    setIsAreYouStillWatchingActive(false);
    onBeforePlay();
    playerActions.play();
  }, [onBeforePlay, playerActions]);

  const openMiniEPG = useCallback(() => {
    setIsMiniEpgOpen(true);
  }, []);

  const closeMiniEPG = useCallback(() => {
    setIsMiniEpgOpen(false);
  }, []);

  const handleMouseEnter = useCallback(() => {
    setUserActive();
  }, [setUserActive]);

  const handleMouseMove = useCallback(() => {
    setUserActive();
  }, [setUserActive]);

  const getLanguageLabel = useCallback((track) => {
    const defaultMessage = formatMessage({ id: 'common.unavailable', defaultMessage: 'Not Available' });

    if (track.isUnknow) {
      return formatMessage(
        {
          id: track.isText
            ? 'languages.undefinedSubtitleWithCounter'
            : 'languages.undefinedAudioWithCounter',
          defaultMessage,
        },
        { number: track.unknownIndex },
      );
    }

    const label = formatMessage({ id: `languages.${track.iso || track.lang}`, defaultMessage });

    if (track.isClosedCaption) return `${label} [CC]`;

    return label;
  }, [formatMessage]);

  const openEpgHandler = useCallback(() => {
    openMiniEPG();
    handleGtmEvent();
  }, [openMiniEPG, handleGtmEvent]);

  const frontwind30 = useCallback(() => {
    const totalFrontWind = !USE_PLAYER_UI_V2 ? SECONDS_TO_FRONTWIND : SECONDS_TO_FRONTWIND_V2;
    shouldReportSeekedEvent.current = false;
    playerActions.rewind(totalFrontWind);
    handleGtmEvent('frontward');
  }, [handleGtmEvent, playerActions]);

  /* When seeked is completed report event to GTM */
  useEffect(() => {
    if (!isSeeking && prevIsSeeking.current) {
      if (shouldReportSeekedEvent.current) handleGtmEvent('timeline');
      else shouldReportSeekedEvent.current = true;
    }
    prevIsSeeking.current = isSeeking;
  }, [isSeeking, handleGtmEvent]);

  /* When playing: schedule hide. Immersive Key Moments: cancel timer, chrome stays up. */
  useEffect(() => {
    if (keyMomentsImmersiveOpen) {
      scheduleSetUserInactive.cancel();
      setIsUserActive(true);
      return;
    }
    if (isPlaying) {
      scheduleSetUserInactive();
    } else {
      scheduleSetUserInactive.cancel();
    }
  }, [isPlaying, keyMomentsImmersiveOpen, scheduleSetUserInactive]);

  /* When displayAreYouStillWatching change schedule display or cancel it */
  useEffect(() => {
    if (displayAreYouStillWatching) {
      scheduleDisplayAreYouStillWatching();
    } else {
      scheduleDisplayAreYouStillWatching.cancel();
    }
  }, [displayAreYouStillWatching, scheduleDisplayAreYouStillWatching]);

  /* Prevent exitting the page if the player is playing */
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (getIsPlaying()) {
        e.preventDefault();
      }
      return null;
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [getIsPlaying]);

  /* Cancel active debounced functions when unmounted (or the debounce function change) */
  useEffect(() => () => {
    scheduleSetUserInactive.cancel();
    scheduleDisplayAreYouStillWatching.cancel();
  }, [scheduleDisplayAreYouStillWatching, scheduleSetUserInactive]);

  useImperativeHandle(ref, () => ({ goToLive }), [goToLive]);

  const LoaderScreenComponent = !USE_PLAYER_UI_V2 ? LoaderScreen : LoaderScreenV2;

  if (isLoading) {
    return (
      <LoaderScreenComponent
        isLive={isLive}
        asset={asset}
        channelName={channelName}
      />
    );
  }

  const hasNextEpiButton = !isLive && asset.episode && hasNextEpi && (
    <NextEpiButtonV2 nextAsset={nextAsset} />
  );

  const hasMoreChannelsButton = isLive && (
    <MoreChannelsButtonV2 openEpgHandler={openEpgHandler} />
  );

  const hasEpisodesButton = !isLive && asset.episode && (
    <EpisodesButtonV2 />
  );

  const ProgressBar = isLive ? LiveProgressBar : VodProgressBar;

  const layoutUIElementsv1 = isMiniEpgOpen ? (
    <MiniEPG
      asset={asset}
      progress={progress}
      close={closeMiniEPG}
    />
  ) : (
    <>
      <ConditionalVisible visible={isUserActive}>
        <PlayerDock
          isLive={isLive}
          asset={asset}
          channelLogo={channelLogo}
          channelName={channelName}
          nextAsset={nextAsset}
          isDelayed={isDelayed}
          gtmInteraction={gtmInteraction}
          openMiniEPG={openMiniEPG}
        />
      </ConditionalVisible>
      <PlayerOverlay
        isUserActive={isUserActive}
        showIconPlayButton={showPlayButton}
        handleToggleFullScreen={toggleFullScreen}
        handleTogglePlayPause={togglePlayPause}
        isBuffering={isBuffering || isTimeShiftLoading}
        tooltip={formatMessage({ id: 'common.continue', defaultMessage: 'Continuar viendo' })}
      />
      <ControlBar className="dtv-web-c-player-ui__control-bar" display={isUserActive}>
        <PlayPauseButton
          handleTogglePlayPause={togglePlayPause}
          isPlaying={!isPaused}
          tooltipPause={formatMessage({ id: 'player.pause' })}
          tooltipPlay={formatMessage({ id: 'player.play' })}
        />
        {!isLive && (
          <RewindButton
            handleRewind={rewind30}
            showButton={!isLive}
            tooltip={formatMessage({ id: 'player.rewind30' })}
          />
        )}
        <ProgressBar
          className="dtv-web-c-player-ui__progress-bar"
          gaps={isLive && keyMoments.enabled ? keyMoments.gaps : undefined}
        />
        {isLive ? (
          <LiveButton
            isDelayed={isDelayed}
            goToLive={goToLive}
            onLiveText={formatMessage({ id: 'player.live', defaultMessage: 'En vivo' })}
          />
        ) : (
          <RemainingTime
            progress={progress < duration ? progress : duration}
            duration={duration}
          />
        )}
        <VolumeButton
          isMuted={volume === 0}
          handleToggleMute={toggleMute}
          setVolume={setVolume}
          volume={volume}
          tooltipMute={formatMessage({ id: 'player.mute' })}
          tooltipUnMute={formatMessage({ id: 'player.unMute' })}
        />
        <div onTouchStart={updateTracks} onMouseEnter={updateTracks}>
          <AudioSubtitleButton
            audioTracks={audioTracks}
            onSelectAudio={selectAudioTrack}
            subtitleTracks={subtitleTracks}
            tooltip={formatMessage({ id: 'player.subtitles' })}
            subtitlesTitle={formatMessage({ id: 'player.subtitles' })}
            audioTitle={formatMessage({ id: 'player.languages' })}
            onSelectSubtitle={selectSubtitleTrack}
            onDisableSubtitles={disableSubtitles}
            getLanguageLabel={getLanguageLabel}
          />
        </div>
        <CastButton />
        <ViewModeButton
          isFullScreenMode={viewMode === PlayerViewMode.FULL_SCREEN}
          handleToggleFullScreen={toggleFullScreen}
          enterFullscreenTooltip={formatMessage({ id: 'player.fullscreen.enter' })}
          exitFullscreenTooltip={formatMessage({ id: 'player.fullscreen.exit' })}
        />
        {asset?.sport?.sportIds?.fifaMatchId && (
          <>
            {readyToPlay && <FifaPlayerNotification currentSchedule={asset} />}
            <FifaPlayerSideButton currentSchedule={asset} />
          </>
        )}
      </ControlBar>
    </>
  );

  const layoutUIElementsv2 = isMiniEpgOpen ? (
    <MiniEPG
      asset={asset}
      progress={progress}
      close={closeMiniEPG}
    />
  ) : (
    <>
      {keyMomentsFocusActive && (
        <KeyMomentsImmersiveHeader onClose={handleDismissKeyMomentsCarousel} />
      )}
      <ConditionalVisible visible={chromeVisible && !keyMomentsFocusActive}>
        <PlayerDockV2
          isLive={isLive}
          asset={asset}
          channelLogo={channelLogo}
          channelName={channelName}
          nextAsset={nextAsset}
          isDelayed={isDelayed}
          gtmInteraction={gtmInteraction}
          openMiniEPG={openEpgHandler}
          hasNextEpi={hasNextEpi}
        />
      </ConditionalVisible>
      <PlayerOverlay
        isUserActive={chromeVisible}
        showIconPlayButton={showPlayButton}
        handleToggleFullScreen={toggleFullScreen}
        handleTogglePlayPause={togglePlayPause}
        isBuffering={isBuffering || isTimeShiftLoading}
        tooltip={formatMessage({ id: 'common.continue', defaultMessage: 'Continuar viendo' })}
      />
      <ControlBar
        className={classnames(
          'dtv-web-c-player-ui-v2__control-bar',
          keyMomentsFocusActive && 'dtv-web-c-player-ui-v2__control-bar--key-moments-focus',
        )}
        display={chromeVisible}
      >
        <div
          className={classnames(
            'dtv-web-c-player-ui-v2__control-bar__wrapper',
            keyMomentsFocusActive && 'dtv-web-c-player-ui-v2__control-bar__wrapper--key-moments-focus',
          )}
        >
          {keyMomentsFocusActive ? (
            <div className="dtv-web-c-player-ui-v2__key-moments-focus">
              <KeyMomentsContainer
                enabled
                immersive
                items={keyMoments.items}
                selectedId={keyMoments.selectedId}
                unavailableIds={keyMoments.unavailableIds}
                isVisible
                onCenterChange={keyMoments.onCenterChange}
                onSelect={handleKeyMomentCarouselSelect}
                onCarouselDismiss={handleDismissKeyMomentsCarousel}
              />
            </div>
          ) : (
            <>
              <div className="dtv-web-c-player-ui-v2__control-progress-bar_box">
                <RemainingTime
                  duration={isLive ? liveProgress : playerTime}
                />
                <div
                  ref={progressBarHoverZoneRef}
                  className={classnames(
                    'dtv-web-c-player-ui-v2__progress-bar-wrapper',
                    keyMoments.enabled && 'dtv-web-c-key-moments-progress-hover-zone',
                  )}
                  data-key-moments-bar={keyMoments.enabled ? 'true' : undefined}
                  onMouseMove={keyMoments.enabled ? handleProgressBarHoverMouseMove : undefined}
                  onMouseLeave={keyMoments.enabled ? handleProgressBarHoverMouseLeave : undefined}
                >
                  <ProgressBar
                    className="dtv-web-c-player-ui-v2__progress-bar"
                    gaps={keyMoments.enabled ? keyMoments.gaps : undefined}
                    extendedSeekEnabled={USE_LL_EXTENDED_SEEK
                      && LL_EXTENDED_SEEK_CHANNELS.has(channelId)}
                    onSeekOutsideWindow={seekToEpoch}
                    streamStartTimestamp={keyMoments.streamStartTimestamp}
                  />
                  {keyMoments.enabled && (
                    <>
                      <KeyMomentsMarkers
                        hitTargetsOnly
                        items={keyMoments.items}
                        duration={liveDuration}
                        streamStartTimestamp={keyMoments.streamStartTimestamp}
                        unavailableIds={keyMoments.unavailableIds}
                        previewIncidenceId={cutPreviewPointer?.incidenceId}
                        onMarkerClick={handleKeyMomentMarkerSelect}
                      />
                      {cutPreviewView && chromeVisible && (
                        <KeyMomentCutPreview
                          item={cutPreviewView.item}
                          leftPercent={cutPreviewView.positionPct}
                          isUnavailable={Boolean(keyMomentRowTouchesUnavailable(
                            cutPreviewView.item,
                            keyMoments.unavailableIds,
                          ))}
                        />
                      )}
                    </>
                  )}
                </div>
                {isLive
                  ? (
                    <RemainingTime
                      progress={liveProgress}
                      duration={liveDuration}
                    />
                  ) : (
                    <RemainingTime
                      progress={playerTime}
                      duration={duration}
                    />
                  )}
              </div>
              <div className="dtv-web-c-player-ui-v2__flux-controls">
                {/* COLUNA ESQUERDA */}
                <div className="group-items-column">
                  <div className="group-items_wrapper">
                    {channelLogo.length > 0 && (
                      <div className="item-channel-logo">
                        <EpgButtonV2 channelLogo={channelLogo} openEpgHandler={openEpgHandler} />
                      </div>
                    )}
                    {isLive && (
                      <LiveButtonV2
                        isDelayed={isDelayed}
                        goToLive={goToLive}
                        onLiveText={formatMessage({ id: isDelayed ? 'player.liveDelayed' : 'player.live' })}
                      />
                    )}
                  </div>
                  {hasNextEpiButton}
                </div>

                {/* COLUNA CENTRAL */}
                <div className="group-items-column__centralize">
                  <div className="group-items_wrapper">
                    <div className="group-items">
                      {
                        !isLive && (
                          <>
                            <RewindTimeButtonV2
                              handleRewind={rewind30}
                              showButton={!isLive}
                              tooltip={formatMessage({ id: 'player.rewind30' })}
                            />
                            <div className="divider-wrapper">
                              <div className="divider" />
                            </div>
                          </>
                        )
                      }
                      <PlayPauseButtonV2
                        handleTogglePlayPause={togglePlayPause}
                        isPlaying={!isPaused}
                        tooltipPause={formatMessage({ id: 'player.pause' })}
                        tooltipPlay={formatMessage({ id: 'player.play' })}
                      />
                      {!isLive && (
                        <>
                          <div className="divider-wrapper">
                            <div className="divider" />
                          </div>
                          <FowardTimeButtonV2
                            handleRewind={frontwind30}
                            showButton={!isLive}
                            tooltip={formatMessage({ id: 'player.frontwind30' })}
                          />
                        </>
                      )}
                      {/* {isLive && (
                    <>
                      <div className="divider-wrapper">
                        <div className="divider" />
                      </div>
                      <RewindTimeLiveButton
                        handleRewind={rewindTotal}
                        showButton={isLive}
                        tooltip={formatMessage({ id: 'player.frontwind30' })}
                      />
                    </>
                    )} */}
                    </div>
                  </div>
                </div>

                {/* COLUNA DIREITA */}
                <div className="group-items-column__centralize">
                  <div className="group-items_wrapper">

                    {/* Grupo 1 — esconde abaixo de 1366px */}
                    <div className={`dtv-player-extended-controls ${viewMode === PlayerViewMode.FULL_SCREEN ? 'dtv-player-extended-controls--fullscreen' : ''}`}>
                      <div className="group-items">
                        {hasMoreChannelsButton}
                        {hasEpisodesButton}
                        {keyMoments.enabled && !isKeyMomentsCarouselOpen && (
                          <>
                            <div className="divider-wrapper">
                              <div className="divider" />
                            </div>
                            <KeyMomentsButton
                              tooltip={formatMessage({
                                id: 'keyMoments.toolbar',
                                defaultMessage: 'Key moments',
                              })}
                              onClick={handleKeyMomentsToolbarToggle}
                              isExpanded={false}
                            />
                          </>
                        )}
                        {asset?.sport?.sportIds?.fifaMatchId && (
                          <>
                            {readyToPlay && <FifaPlayerNotification currentSchedule={asset} />}
                            <FifaPlayerSideButton currentSchedule={asset} />
                          </>
                        )}
                      </div>
                    </div>

                    {/* Grupo 2 — volume e legendas escondem, viewmode sempre aparece */}
                    <div className="group-items">
                      <div className={`dtv-player-extended-controls ${viewMode === PlayerViewMode.FULL_SCREEN ? 'dtv-player-extended-controls--fullscreen' : ''}`}>
                        <VolumeButtonV2
                          isMuted={volume === 0}
                          handleToggleMute={toggleMute}
                          setVolume={setVolume}
                          volume={volume}
                          tooltipMute={formatMessage({ id: 'player.mute' })}
                          tooltipUnMute={formatMessage({ id: 'player.unMute' })}
                        />
                        <div className="divider-wrapper">
                          <div className="divider" />
                        </div>
                        <div onTouchStart={updateTracks} onMouseEnter={updateTracks}>
                          <AudioSubtitleButtonV2
                            audioTracks={audioTracks}
                            onSelectAudio={selectAudioTrack}
                            subtitleTracks={subtitleTracks}
                            tooltip={formatMessage({ id: 'player.subtitles' })}
                            subtitlesTitle={formatMessage({ id: 'player.subtitles' })}
                            audioTitle={formatMessage({ id: 'player.languages' })}
                            onSelectSubtitle={selectSubtitleTrack}
                            onDisableSubtitles={disableSubtitles}
                            getLanguageLabel={getLanguageLabel}
                          />
                        </div>
                        <div className="divider-wrapper">
                          <div className="divider" />
                        </div>
                      </div>
                      <ViewModeButtonV2
                        isFullScreenMode={viewMode === PlayerViewMode.FULL_SCREEN}
                        handleToggleFullScreen={toggleFullScreen}
                        enterFullscreenTooltip={formatMessage({ id: 'player.fullscreen.enter' })}
                        exitFullscreenTooltip={formatMessage({ id: 'player.fullscreen.exit' })}
                      />
                      {asset?.sport?.sportIds?.fifaMatchId && (
                        <>
                          {readyToPlay && <FifaPlayerNotification currentSchedule={asset} />}
                          <FifaPlayerSideButton currentSchedule={asset} />
                        </>
                      )}
                    </div>

                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </ControlBar>
    </>
  );

  const uiElements = !USE_PLAYER_UI_V2 ? layoutUIElementsv1 : layoutUIElementsv2;
  const playerUIclassName = !USE_PLAYER_UI_V2 ? 'dtv-web-c-player-ui' : 'dtv-web-c-player-ui-v2';

  return (
    <div
      className={classnames(playerUIclassName)}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
    >
      {isAreYouStillWatchingActive ? (
        <AreYouStillWatchingMessage
          title={!isLive && asset?.type === assetTypes.episode && asset?.episode?.showName}
          continueWatching={continueWatching}
        />
      ) : uiElements}
    </div>
  );
});

PlayerUI.propTypes = {
  isLive: PropTypes.bool,
  asset: PropTypes.shape({
    type: PropTypes.string,
    episode: PropTypes.shape({
      showName: PropTypes.string,
    }),
    sport: PropTypes.shape({
      sportIds: PropTypes.shape({
        fifaMatchId: PropTypes.string,
      }),
    }),
  }),
  channelId: PropTypes.string,
  channelName: PropTypes.string,
  channelLogo: PropTypes.arrayOf(PropTypes.shape({})),
  nextAsset: PropTypes.shape({}),
  readyToPlay: PropTypes.bool,
  displayAreYouStillWatching: PropTypes.bool,
  showPlayButton: PropTypes.bool,
  gtmInteraction: PropTypes.func,
  onUserInteraction: PropTypes.func,
  onGoLive: PropTypes.func,
  onBeforePlay: PropTypes.func,
  authorizerVrioId: PropTypes.string,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  hasNextEpi: PropTypes.bool,
  nextAssetIsLoading: PropTypes.bool,
  seekToEpoch: PropTypes.func,
  isTimeShiftLoadingRef: PropTypes.shape({ current: PropTypes.bool }),
  onTimeShiftLoadComplete: PropTypes.func,
};

PlayerUI.defaultProps = {
  isLive: false,
  asset: {},
  channelId: '',
  channelName: '',
  channelLogo: [],
  nextAsset: {},
  readyToPlay: false,
  displayAreYouStillWatching: false,
  showPlayButton: false,
  gtmInteraction: () => {},
  onUserInteraction: () => {},
  onGoLive: () => {},
  onBeforePlay: () => {},
  authorizerVrioId: '',
  hasNextEpi: false,
  nextAssetIsLoading: false,
  seekToEpoch: undefined,
  isTimeShiftLoadingRef: undefined,
  onTimeShiftLoadComplete: () => {},
};

export default PlayerUI;
